import {
  Button,
  Card,
  CreateButton,
  Icons,
  Space,
  Table,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { CustomDrawer } from 'components/drawer';
import { ISale } from 'interfaces';
import { useEffect, useState } from 'react';
import { Action, ActionKind, IReturnProduct } from 'reducers/product-return-reducer';
import { AddReturnProduct } from './AddReturnProduct';
import { EditReturnProduct } from './EditReturnProduct';
import { IVariant, getVariant } from 'services/variant';

type ProductReturnTableProps = {
  options: ISale['salesProductsCopy'];
  returnProducts: IReturnProduct[];
  productDispatcher: React.Dispatch<Action>;
  invoiceDiscount: number
};

const { Title } = Typography;

export const ProductReturnTable: React.FC<ProductReturnTableProps> = (props) => {
  const [editReturnProduct, setEditReturnProduct] = useState<IReturnProduct | null>(null);
  const [isAddDrawerVisible, setIsAddDrawerVisible] = useState<boolean>(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState<boolean>(false);
  const [isInitialDataLoading, setIsInitialDataLoading] = useState<boolean>(false);
  const [selectedVariant, setSelectedVariant] = useState<IVariant | null>(null);

  useEffect(() => {
    (async () => {
      if (!props.options.length) return;

      setIsInitialDataLoading(true);
      try {
        const variant = await getVariant(props.options[0].productVariant.id);
        setSelectedVariant(variant);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsInitialDataLoading(false);
    })();
  }, [props.options]);

  const cardTitle = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        width: '100%',
        whiteSpace: 'normal',
      }}
    >
      <Title level={4} style={{ margin: 0 }}>
        Add product
      </Title>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <CreateButton type="primary" onClick={() => setIsAddDrawerVisible(true)}>Add New</CreateButton>
      </div>
    </div>
  );

  return (
    <>
      {/* Add Drawer Form */}
      <CustomDrawer
        title="Add Product"
        visible={isAddDrawerVisible}
        onClose={() => setIsAddDrawerVisible(false)}
      >
        <AddReturnProduct
          isInitialDataLoading={isInitialDataLoading}
          selectedVariant={selectedVariant}
          options={props.options}
          returnProducts={props.returnProducts}
          onSubmit={(product: IReturnProduct) => {
            setIsAddDrawerVisible(false);
            props.productDispatcher({ type: ActionKind.AddReturnProduct, payload: product });
          }}
          invoiceDiscount={props.invoiceDiscount}
        />
      </CustomDrawer>

      {/* Edit Drawer Form */}
      {editReturnProduct && (
        <CustomDrawer
          title="Edit Product"
          visible={isEditDrawerVisible}
          onClose={() => setIsEditDrawerVisible(false)}
        >
          <EditReturnProduct
            editProduct={editReturnProduct}
            options={props.options}
            onSubmit={(product: IReturnProduct) => {
              setIsEditDrawerVisible(false);
              props.productDispatcher({
                type: ActionKind.EditReturnProduct,
                payload: product,
              });
            }}
          />
        </CustomDrawer>
      )}

      <Card title={cardTitle}>
        <Table dataSource={props.returnProducts} rowKey="sku" scroll={{ x: true }}>
          <Table.Column dataIndex="sku" title="SKU" />
          <Table.Column dataIndex="productVariantName" title="Variant Name" />
          <Table.Column dataIndex="quantity" title="Quantity" />
          <Table.Column dataIndex="unitPrice" title="Selling Price" />
          <Table.Column<IReturnProduct>
            title="Actions"
            dataIndex="actions"
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <Button
                    size="small"
                    icon={<Icons.EditOutlined />}
                    onClick={() => {
                      setIsEditDrawerVisible(true);
                      setEditReturnProduct(record);
                    }}
                  />
                  <Button
                    size="small"
                    danger
                    icon={<Icons.DeleteOutlined />}
                    onClick={() =>
                      props.productDispatcher({
                        type: ActionKind.RemoveReturnProduct,
                        payload: record.productVariantId,
                      })
                    }
                  />
                </Space>
              );
            }}
          />
        </Table>
      </Card>
    </>
  );
};
